import { computed, inject, provide, ref, toRef, watch } from 'vue';

import { propsFactory, warn } from '@components/utils';

import { useProxyModel } from './proxyModel';

import type { IValidationProps } from './validation';
import type { Ref, ComputedRef, PropType, InjectionKey } from 'vue';

export interface IFormProvide {
  register: (item: {
    id: number | string;
    validate: () => Promise<string[]>;
    reset: () => void;
    resetValidation: () => void;
  }) => void;
  unregister: (id: number | string) => void;
  update: (id: number | string, isValid: boolean | null, errorMessages: string[]) => void;
  items: Ref<IFormField[]>;
  isDisabled: ComputedRef<boolean>;
  isReadonly: ComputedRef<boolean>;
  isValidating: Ref<boolean>;
  validateOn: Ref<IFormProps['validateOn']>;
}

export interface IFormProps {
  label: string;
  description: string;
  disabled: boolean;
  readonly: boolean;
  firstFail: boolean;
  modelValue: boolean | null;
  validateOn: IValidationProps['validateOn'];
}

interface IFormField {
  id: number | string;
  validate: () => Promise<string[]>;
  reset: () => void;
  resetValidation: () => void;
  isValid: boolean | null;
  errorMessages: string[];
}

interface IFieldValidationResult {
  id: number | string;
  errorMessages: string[];
}

interface IFormValidationResult {
  valid: boolean;
  errors: IFieldValidationResult[];
}

export interface SubmitEventPromise extends SubmitEvent, Promise<IFormValidationResult> {}

export const makeFormProps = propsFactory(
  {
    /**
     * Заголовок формы
     */
    label: {
      type: String as PropType<string>,
      default: ''
    },
    /**
     * Описание формы
     */
    description: {
      type: String as PropType<string>,
      default: ''
    },
    /**
     * Заблокировать форму
     */
    disabled: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Сделать форму только для чтения
     */
    readonly: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Остановить валидацию при первой ошибке
     */
    firstFail: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Значение модели
     */
    modelValue: {
      type: Boolean as PropType<boolean | null>,
      default: null
    },
    /**
     * Событие для валидации
     */
    validateOn: {
      type: String as PropType<IFormProps['validateOn']>,
      default: 'input'
    }
  },
  'form'
);

export const FormKey: InjectionKey<IFormProvide> = Symbol.for('cds:form');

export function createForm(props: IFormProps) {
  const model = useProxyModel(props, 'modelValue');
  const isDisabled = computed(() => props.disabled);
  const isReadonly = computed(() => props.readonly);
  const isValidating = ref(false);
  const items = ref<IFormField[]>([]);
  const errors = ref<IFieldValidationResult[]>([]);

  async function validate() {
    const results = [];
    let valid = true;

    errors.value = [];
    isValidating.value = true;

    for (const item of items.value) {
      const itemErrorMessage = await item.validate();

      if (itemErrorMessage.length > 0) {
        valid = false;

        results.push({
          id: item.id,
          errorMessages: itemErrorMessage
        });
      }

      if (!valid && props.firstFail) break;
    }

    errors.value = results;
    isValidating.value = false;

    return { valid, errors: errors.value };
  }

  function reset() {
    items.value.forEach((item) => item.reset());
    model.value = null;
  }

  function resetValidation() {
    items.value.forEach((item) => item.resetValidation());
    errors.value = [];
    model.value = null;
  }

  watch(
    items,
    () => {
      let valid = 0;
      let invalid = 0;
      const results = [];

      for (const item of items.value) {
        if (item.isValid === false) {
          invalid++;
          results.push({
            id: item.id,
            errorMessages: item.errorMessages
          });
        } else if (item.isValid === true) {
          valid++;
        }
      }

      errors.value = results;
      model.value = invalid > 0 ? false : valid === items.value.length ? true : null;
    },
    { deep: true }
  );

  provide(FormKey, {
    register: ({ id, validate, reset, resetValidation }) => {
      if (items.value.some((item) => item.id === id)) {
        warn('form', `Field with id "${id}" already exists.`);
      }

      items.value.push({
        id,
        validate,
        reset,
        resetValidation,
        isValid: null,
        errorMessages: []
      });
    },
    unregister: (id) => {
      items.value = items.value.filter((item) => item.id !== id);
    },
    update: (id, isValid, errorMessages) => {
      const found = items.value.find((item) => item.id === id);

      if (!found) return;

      found.isValid = isValid;
      found.errorMessages = errorMessages;
    },
    isDisabled,
    isReadonly,
    isValidating,
    items,
    validateOn: toRef(props, 'validateOn')
  });

  return {
    errors,
    isDisabled,
    isReadonly,
    isValidating,
    model,
    validate,
    reset,
    resetValidation
  };
}

export function useForm() {
  return inject(FormKey, null);
}
